import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';

interface Progress {
  clients: number;
  quotations: { total: number; finalized: number };
  billing: unknown[];
}

const hiddenKey = (companyId: string) => `expertdevis.demarrage-masque.${companyId}`;
const readHidden = (companyId: string) => {
  try {
    return localStorage.getItem(hiddenKey(companyId)) === '1';
  } catch {
    return false;
  }
};

/** « Bien démarrer » : chaque étape est cochée d'après les données réelles de l'entreprise, jamais à la main. */
export function StartChecklist({ data }: { data: Progress }) {
  const { user, can } = useAuth();
  const companyId = user?.companyId ?? '';
  const [hidden, setHidden] = useState(() => readHidden(companyId));
  const manage = can('company:manage');
  const company = useQuery({
    queryKey: ['company'],
    queryFn: () => api<{ address: string | null; phone: string | null; logo: string | null }>('/company'),
    enabled: manage && !hidden,
  });

  if (hidden) return null;
  const steps = [
    ...(manage ? [{ label: 'Compléter votre entreprise : adresse, téléphone, logo', done: !!(company.data?.address && company.data.phone && company.data.logo), to: '/parametres/entreprise', help: 'entreprise' }] : []),
    ...(can('project:edit') ? [{ label: 'Ajouter un premier client', done: data.clients > 0, to: '/clients?nouveau=1', help: 'clients' }] : []),
    ...(can('quotation:edit') && can('project:create') ? [{ label: 'Créer votre premier devis', done: data.quotations.total > 0, to: '/devis/rapide', help: 'devis-rapide' }] : []),
    ...(can('quotation:finalize') ? [{ label: 'Finaliser un devis et télécharger son PDF', done: data.quotations.finalized > 0, to: '/devis', help: 'suivi-devis' }] : []),
    ...(can('invoice:edit') ? [{ label: 'Établir une première facture', done: data.billing.length > 0, to: '/factures/nouvelle', help: 'facture' }] : []),
  ];
  const remaining = steps.filter((s) => !s.done).length;
  if (!steps.length || remaining === 0 || (manage && company.isPending)) return null;

  const hide = () => {
    try {
      localStorage.setItem(hiddenKey(companyId), '1');
    } catch {
      // Stockage indisponible : la liste est masquée pour cette visite seulement.
    }
    setHidden(true);
  };

  return (
    <section className="card mb-6 p-5" aria-label="Bien démarrer">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-navy-900">Bien démarrer</h2>
          <p className="text-sm text-muted">{steps.length - remaining} étape{steps.length - remaining > 1 ? 's' : ''} sur {steps.length} · chaque étape se coche d'elle-même</p>
        </div>
        <button type="button" className="btn-ghost px-2" aria-label="Masquer cette liste" onClick={hide}><X size={16} /></button>
      </div>
      <ul className="mt-3 space-y-2 text-sm">
        {steps.map((s) => (
          <li key={s.label} className="flex items-start gap-2">
            {s.done ? <CheckCircle2 size={18} aria-hidden="true" className="mt-0.5 shrink-0 text-ok" /> : <Circle size={18} aria-hidden="true" className="mt-0.5 shrink-0 text-muted" />}
            <span className="min-w-0 flex-1">
              {s.done ? <span className="text-muted line-through">{s.label}</span> : <Link to={s.to} className="font-medium text-tech-600 hover:underline">{s.label}</Link>}
              {!s.done && <Link to={`/aide?sujet=${s.help}`} className="ml-2 text-xs text-muted hover:underline">Comment faire ?</Link>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
